import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { insertHtmlAtSelection } from "@/features/study-group-events/components/utils/contentEditableHelpers";
import {
  buildInviteMessageHtml,
  buildStudyGroupNameBadgeHtml,
  clampInvitationMessage,
  insertStudyGroupToken,
  toStoredInviteMessage,
} from "@/features/study-group-info/utils/invitationMessage";
import { cn } from "@/lib/utils";

interface InvitationMessageEditorProps {
  id?: string;
  value: string;
  placeholder: string;
  maxLength: number;
  disabled?: boolean;
  className?: string;
  "aria-labelledby"?: string;
  badgeLabel?: string;
  onChange: (value: string) => void;
}

export interface InvitationMessageEditorHandle {
  insertStudyGroupPlaceholder: () => void;
}

const placeCaretAtEnd = (element: HTMLDivElement) => {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

export const InvitationMessageEditor = forwardRef<
  InvitationMessageEditorHandle,
  InvitationMessageEditorProps
>(
  (
    {
      id,
      value,
      placeholder,
      maxLength,
      disabled = false,
      className,
      "aria-labelledby": ariaLabelledby,
      badgeLabel,
      onChange,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const editorRef = useRef<HTMLDivElement>(null);
    const isUserEditingRef = useRef(false);
    const isComposingRef = useRef(false);

    const fallbackLabel = t(
      "studyGroup.inviteMessageStudyGroupNamePlaceholder",
    );
    const badgeText = badgeLabel?.trim() ? badgeLabel : fallbackLabel;
    const badgeHtml = useMemo(
      () => buildStudyGroupNameBadgeHtml(badgeText),
      [badgeText],
    );
    const displayHtml = useMemo(
      () => buildInviteMessageHtml(value, badgeText),
      [badgeText, value],
    );

    useEffect(() => {
      if (!editorRef.current || isUserEditingRef.current) return;
      if (editorRef.current.innerHTML !== displayHtml) {
        editorRef.current.innerHTML = displayHtml;
      }
    }, [displayHtml]);

    const commitChange = useCallback(
      ({
        sourceHtml,
        syncDom = false,
      }: {
        sourceHtml?: string;
        syncDom?: boolean;
      } = {}) => {
        const html = sourceHtml ?? editorRef.current?.innerHTML ?? "";
        const rawStored = toStoredInviteMessage(html);
        const stored = clampInvitationMessage(rawStored, maxLength);
        const wasClamped = rawStored.length > stored.length;
        // Replacing innerHTML while focused can reset the caret in contentEditable,
        // especially after IME composition confirmation (e.g. Japanese Enter key).
        // Still sync immediately when maxLength clamping changed the stored value,
        // otherwise overflow text remains visible until blur.
        const shouldSyncDom =
          editorRef.current &&
          (wasClamped ||
            (syncDom && document.activeElement !== editorRef.current));
        if (shouldSyncDom && editorRef.current) {
          const nextHtml = buildInviteMessageHtml(stored, badgeText);
          if (editorRef.current.innerHTML !== nextHtml) {
            editorRef.current.innerHTML = nextHtml;
          }
        }
        if (stored !== value) onChange(stored);
      },
      [badgeText, maxLength, onChange, value],
    );

    const insertPlaceholderAtCursor = useCallback(() => {
      const editorEl = editorRef.current;
      if (!editorEl) return;
      insertHtmlAtSelection(
        editorEl,
        badgeHtml,
        () => {
          const next = clampInvitationMessage(
            insertStudyGroupToken(value),
            maxLength,
          );
          onChange(next);
          if (editorRef.current) {
            editorRef.current.innerHTML = buildInviteMessageHtml(
              next,
              badgeText,
            );
            placeCaretAtEnd(editorRef.current);
          }
        },
        () => commitChange({ sourceHtml: editorEl.innerHTML }),
      );
    }, [badgeHtml, badgeText, commitChange, maxLength, onChange, value]);

    useImperativeHandle(
      ref,
      () => ({
        insertStudyGroupPlaceholder: insertPlaceholderAtCursor,
      }),
      [insertPlaceholderAtCursor],
    );

    return (
      // biome-ignore lint/a11y/useSemanticElements: contentEditable is required for badge placeholders
      <div
        id={id}
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-labelledby={ariaLabelledby}
        data-placeholder={placeholder}
        contentEditable={!disabled}
        tabIndex={disabled ? -1 : 0}
        onFocus={() => {
          isUserEditingRef.current = true;
        }}
        onBlur={() => {
          isUserEditingRef.current = false;
          commitChange({ syncDom: true });
        }}
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={() => {
          isComposingRef.current = false;
          commitChange({ syncDom: true });
        }}
        onInput={() => {
          if (!isComposingRef.current) {
            commitChange();
          }
        }}
        className={cn(
          "relative min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base leading-6 outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm",
          "[&:empty]:before:pointer-events-none [&:empty]:before:text-muted-foreground [&:empty]:before:content-[attr(data-placeholder)]",
          "whitespace-pre-wrap break-words",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
        spellCheck
      />
    );
  },
);

InvitationMessageEditor.displayName = "InvitationMessageEditor";
